// @ts-nocheck
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

export interface ScoutCalendarEvent {
  id: string;
  title: string;
  description?: string;
  date: string;
  time?: string;
  location?: string;
  type: 'assigned_task' | 'personal_task';
  status: 'scheduled' | 'confirmed' | 'pending' | 'cancelled';
  athleteName?: string;
  athleteId?: string;
  athleteColor?: string;
}

const ATHLETE_COLORS = [
  '#0891b2', // cyan
  '#059669', // emerald
  '#7c3aed', // violet
  '#dc2626', // red
  '#ea580c', // orange
  '#0d9488', // teal
  '#7c2d12', // amber
  '#1d4ed8', // blue
];

export const useScoutCalendarEvents = () => {
  const { profile } = useAuth();
  const [events, setEvents] = useState<ScoutCalendarEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [athleteColors, setAthleteColors] = useState<Record<string, string>>({});

  const assignAthleteColor = (athleteId: string): string => {
    if (athleteColors[athleteId]) {
      return athleteColors[athleteId];
    }
    
    const colorIndex = Object.keys(athleteColors).length % ATHLETE_COLORS.length;
    const color = ATHLETE_COLORS[colorIndex];
    
    setAthleteColors(prev => ({
      ...prev,
      [athleteId]: color
    }));
    
    return color;
  };

  const fetchScoutEvents = async () => {
    if (!profile?.user_id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Fetch assigned tasks from scout_task_assignments
      const { data: assignedTasks, error: assignedError } = await supabase
        .from('scout_task_assignments')
        .select(`
          *,
          scout_task:scout_tasks!task_id (
            id,
            title,
            description,
            due_date
          ),
          athlete_profile:profiles!athlete_profile_id (
            id,
            name
          )
        `)
        .eq('assigned_by', profile.id)
        .neq('status', 'completed')
        .order('assigned_at', { ascending: false });

      if (assignedError) {
        console.error('Error fetching assigned tasks:', assignedError);
        return;
      }

      // Fetch personal calendar events for the scout
      const { data: personalEvents, error: personalError } = await supabase
        .from('calendar_events')
        .select('*')
        .eq('profile_id', profile.id)
        .order('start_date', { ascending: true });

      if (personalError) {
        console.error('Error fetching personal events:', personalError);
        return;
      }

      // Transform assigned tasks
      const transformedAssignedTasks: ScoutCalendarEvent[] = (assignedTasks || []).map(assignment => {
        const task = Array.isArray(assignment.scout_task) ? assignment.scout_task[0] : assignment.scout_task;
        const athlete = Array.isArray(assignment.athlete_profile) ? assignment.athlete_profile[0] : assignment.athlete_profile;
        const dueDate = assignment.due_date || task?.due_date;
        
        if (!dueDate || !athlete) return null;

        const athleteColor = assignAthleteColor(assignment.athlete_profile_id);

        return {
          id: `assigned-${assignment.id}`,
          title: task?.title || 'Scout Task',
          description: task?.description || 'Task assigned to athlete',
          date: dueDate,
          type: 'assigned_task',
          status: assignment.status as any || 'pending',
          athleteName: athlete.name || 'Unknown Athlete',
          athleteId: assignment.athlete_profile_id,
          athleteColor
        };
      }).filter(Boolean) as ScoutCalendarEvent[];

      // Transform personal events
      const transformedPersonalEvents: ScoutCalendarEvent[] = (personalEvents || []).map(event => ({
        id: `personal-${event.id}`,
        title: event.title,
        description: event.description,
        date: event.start_date,
        location: event.source || undefined,
        type: 'personal_task',
        status: 'scheduled'
      }));

      setEvents([...transformedAssignedTasks, ...transformedPersonalEvents]);
    } catch (error) {
      console.error('Error in fetchScoutEvents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchScoutEvents();
  }, [profile?.user_id, profile?.id]);

  return {
    events,
    isLoading,
    athleteColors,
    refreshEvents: fetchScoutEvents
  };
};
